import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import Button from "../UI/Button";

const HeroContainer = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 6rem 0 4rem;
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.gradient};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 90vh;
    padding: 4rem 0 2rem;
  }
`;

const HeroContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 0 1rem;
    gap: 1.5rem;
  }
`;

const HeroText = styled.div`
  z-index: 2;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    order: 2;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  background: ${({ theme }) => theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: clamp(2rem, 8vw, 3rem);
    margin-bottom: 1rem;
    line-height: 1.2;
  }
`;

const HeroSubtitle = styled(motion.div)`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primary.cyan};
  margin-bottom: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1.2rem;
    letter-spacing: 1px;
    margin-bottom: 0.75rem;
  }
`;

const ConferenceYear = styled(motion.div)`
  font-size: 3rem;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.primary.yellow};
  text-shadow: 0 0 30px rgba(255, 229, 0, 0.5);
  margin-bottom: 1rem;
  font-family: ${({ theme }) => theme.fonts.mono};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
    text-shadow: 0 0 20px rgba(255, 229, 0, 0.4);
  }
`;

const HeroDescription = styled(motion.p)`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 2rem;
  font-size: 1.1rem;
  line-height: 1.7;
  max-width: 500px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    padding: 0 0.5rem;
  }
`;

const HeroCTA = styled(motion.div)`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    max-width: 300px;
    margin: 0 auto;
  }
`;

const HeroIllustrationContainer = styled(motion.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    order: 1;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 300px;
  }
`;

const HeroIllustrate = styled.img`
  width: 100%;
  height: auto;
  max-width: 600px;
  max-height: 500px;
  object-fit: contain;
  border-radius: 12px;
  filter: drop-shadow(0 10px 30px rgba(0, 128, 214, 0.3));
  transition: transform 0.3s ease, filter 0.3s ease;

  &:hover {
    transform: scale(1.02);
    filter: drop-shadow(0 15px 40px rgba(0, 128, 214, 0.4));
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: 100%;
    max-height: 280px;
    border-radius: 8px;
    filter: drop-shadow(0 5px 20px rgba(0, 128, 214, 0.25));
    
    &:hover {
      transform: none;
      filter: drop-shadow(0 5px 20px rgba(0, 128, 214, 0.25));
    }
  }
`;

const BackgroundElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    opacity: 0.6;
  }
`;

const CyberShape = styled(motion.div)`
  position: absolute;
  border: 2px solid ${({ color }) => color || "#00D4FF"};
  background: transparent;
  opacity: 0.3;

  &.hexagon {
    width: 60px;
    height: 60px;
    clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
  }

  &.triangle {
    width: 0;
    height: 0;
    border-left: 30px solid transparent;
    border-right: 30px solid transparent;
    border-bottom: 52px solid ${({ color }) => color || "#00D4FF"};
    background: transparent;
  }

  &.diamond {
    width: 40px;
    height: 40px;
    transform: rotate(45deg);
  }

  &.shield {
    width: 50px;
    height: 60px;
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    &.hexagon {
      width: 40px;
      height: 40px;
    }

    &.triangle {
      border-left: 20px solid transparent;
      border-right: 20px solid transparent;
      border-bottom: 35px solid ${({ color }) => color || "#00D4FF"};
    }

    &.diamond {
      width: 30px;
      height: 30px;
    }

    &.shield {
      width: 35px;
      height: 42px;
    }
  }
`;

const ZiaPattern = styled(motion.div)`
  position: absolute;
  width: 80px;
  height: 80px;
  border: 2px solid #ffe500;
  border-radius: 50%;
  opacity: 0.2;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #ffe500;
    opacity: 0.4;
  }

  &::before {
    width: 100px;
    height: 2px;
  }

  &::after {
    width: 2px;
    height: 100px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 60px;
    height: 60px;
    
    &::before {
      width: 75px;
      height: 1.5px;
    }

    &::after {
      width: 1.5px;
      height: 75px;
    }
  }
`;

const NetworkLine = styled(motion.div)`
  position: absolute;
  height: 1px;
  background: linear-gradient(90deg, transparent, #00d4ff, transparent);
  opacity: 0.4;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 0.5px;
    opacity: 0.3;
  }
`;

const Hero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const cyberShapes = [
    { type: "hexagon", top: "10%", left: "15%", color: "#00D4FF", delay: 0 },
    {
      type: "triangle",
      top: "20%",
      right: "20%",
      color: "#FFE500",
      delay: 0.5,
    },
    { type: "diamond", bottom: "25%", left: "10%", color: "#9B59B6", delay: 1 },
    { type: "shield", top: "60%", right: "15%", color: "#0080D6", delay: 1.5 },
    {
      type: "hexagon",
      bottom: "15%",
      right: "25%",
      color: "#00D4FF",
      delay: 2,
    },
  ];

  const networkLines = [
    { top: "25%", left: "20%", width: "200px", angle: "15deg", delay: 2.5 },
    { top: "55%", left: "10%", width: "180px", angle: "-25deg", delay: 3 },
    { bottom: "30%", right: "20%", width: "150px", angle: "35deg", delay: 3.5 },
  ];

  return (
    <HeroContainer>
      {/* Background Elements */}
      <BackgroundElements>
        {/* Zia Sun Patterns */}
        <ZiaPattern
          style={{ top: "15%", right: "10%" }}
          animate={{ rotate: 360 }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        />
        <ZiaPattern
          style={{ bottom: "20%", left: "5%" }}
          animate={{ rotate: -360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        />

        {/* Cybersecurity Shapes */}
        {cyberShapes.map((shape, index) => (
          <CyberShape
            key={index}
            className={shape.type}
            color={shape.color}
            style={{
              top: shape.top,
              left: shape.left,
              right: shape.right,
              bottom: shape.bottom,
            }}
            animate={{
              y: [0, -20, 0],
              rotate: shape.type === "diamond" ? [45, 135, 45] : [0, 360, 0],
            }}
            transition={{
              duration: 6 + index,
              repeat: Infinity,
              ease: "easeInOut",
              delay: shape.delay,
            }}
          />
        ))}

        {/* Network Connection Lines */}
        {networkLines.map((line, index) => (
          <NetworkLine
            key={index}
            style={{
              top: line.top,
              left: line.left,
              right: line.right,
              bottom: line.bottom,
              width: line.width,
              transform: `rotate(${line.angle})`,
              transformOrigin: "left center",
            }}
            animate={{
              opacity: [0.2, 0.8, 0.2],
              scaleX: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: line.delay,
            }}
          />
        ))}
      </BackgroundElements>

      <div className="container">
        <HeroContent>
          <HeroText>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <HeroTitle variants={itemVariants}>
                CYBERSECURITY
                <br />
                CONFERENCE
              </HeroTitle>

              <ConferenceYear variants={itemVariants}>2025</ConferenceYear>

              <HeroSubtitle variants={itemVariants}>
                🔐 Security BSides Albuquerque
              </HeroSubtitle>

              <HeroDescription variants={itemVariants}>
                Join the most innovative cybersecurity conference where security
                enthusiasts and experts come together to share knowledge,
                exchange ideas, and strengthen our collective security awareness
                in a collaborative and inclusive environment.
              </HeroDescription>

              <HeroCTA variants={itemVariants}>
                <Button
                  variant="primary"
                  size="large"
                  href="https://www.eventbrite.com/e/security-bsides-albuquerque-cybersecurity-conference-tickets-1244346185939"
                >
                  🎫 Secure Your Spot
                </Button>
              </HeroCTA>
            </motion.div>
          </HeroText>

          <HeroIllustrationContainer
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 1.2,
              delay: 0.5,
              ease: "easeOut",
            }}
          >
            <HeroIllustrate
              src="/heroimage.webp"
              alt="BSides Albuquerque Cybersecurity Conference"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentNode.innerHTML = `
                  <div style="
                    width: 100%; 
                    height: 400px; 
                    background: linear-gradient(135deg, #0080D6, #FFE500); 
                    border-radius: 12px; 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                    color: white; 
                    font-size: 18px;
                    font-weight: bold;
                    max-height: 280px;
                  ">
                    🔐 Cybersecurity Hero Image
                  </div>
                `;
              }}
            />
          </HeroIllustrationContainer>
        </HeroContent>
      </div>
    </HeroContainer>
  );
};

export default Hero;